import React, { Component } from 'react';
import { Button, Form, Alert, Card } from 'react-bootstrap';
import { GoX, GoPlay } from 'react-icons/go';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/theme-monokai';
import Select from 'components/controls/select';
import StatusPill from '../../components/statusPill';
import MetaThread from './metaThread';
import ActionCard, { ActionCards } from '../../../../../controls/common/actionCard';
import { upsertCell } from '../../domain/model';

/**
 * CellPanel — right-docked detail panel for a selected cell.
 *
 * Sections (top → bottom):
 *   1. Header        — the static word "Cell" + close button (the cell's own title lives below)
 *   2. Properties    — Status pill / Workflow / Workspace gray-chip table
 *   3. Actions       — the applicable actions for the cell's status, one compact card each; the
 *        whole section (heading + hr) is hidden when no action applies:
 *        draft        → Activate Cell (client.activateCell)
 *        input_needed → Answer Input form (client.answerInput); when props.input_needed.kind === "review",
 *                       splits into separate Approve / Request Changes cards instead
 *        failed       → Retry (client.activateCell)
 *   4. Details       — cell.props.title + cell.props.description; editable (text input +
 *        markdown Ace editor) only while status === "draft", read-only otherwise. Saved on blur
 *        via client.setCellProps then an incremental onApplyLocal(upsertCell); the write is skipped
 *        when the value is unchanged.
 *   5. Cell-Workflow mini-graph  — step chain, current step highlighted
 *   6. Meta-thread  — chronological event timeline
 *
 * Every write here is INCREMENTAL: it calls the client then applies a pure store
 * reducer via onApplyLocal — never a full reload. The panel stays open and the
 * mission graph updates in place.
 *
 * Props:
 *   cell         — cell vertex object from the store
 *   missionId    — string
 *   client       — SystemOfWorkClient instance
 *   onClose      — () => void
 *   onApplyLocal — (updater: store => store) => void — incremental local store update
 *   templates    — store.templates
 */

// ---------------------------------------------------------------------------
// WorkflowMiniGraph — simplified step chain (no xyflow, just flex boxes)
// ---------------------------------------------------------------------------

function WorkflowMiniGraph({ workflow, currentStep }) {
  if (!workflow) {
    return <p className="text-muted">No cell-workflow assigned.</p>;
  }

  const nodes = workflow.nodes || [];   // list: [{ id, ty|type, ... }]
  const edges = workflow.edges || [];   // list: [{ from, to, when? }]

  if (nodes.length === 0) {
    return <p className="text-muted">Workflow has no steps.</p>;
  }

  const byId = {};
  nodes.forEach(n => { if (n && n.id) byId[n.id] = n; });

  // Build a simple ordered list by following edges from the start step
  const order = [];
  const visited = new Set();
  const startId = workflow.start || (nodes[0] && nodes[0].id);

  function walk(id) {
    if (!id || visited.has(id) || id === '@done') return;
    visited.add(id);
    order.push(id);
    const nextEdge = edges.find(e => e.from === id);
    if (nextEdge) walk(nextEdge.to);
  }

  walk(startId);

  // Append any steps not reached via walk (disconnected or parallel)
  for (const n of nodes) {
    if (n && n.id && !visited.has(n.id)) order.push(n.id);
  }

  return (
    <div className="mc-mini-wf">
      {order.map((id, idx) => {
        const node = byId[id] || {};
        const isCurrent = id === currentStep;
        const isPast = currentStep && order.indexOf(currentStep) > idx;

        return (
          <React.Fragment key={id}>
            <div className={`mc-mini-step${isCurrent ? ' current' : ''}${isPast ? ' past' : ''}`}>
              <span className="bh-node-label mc-mini-step-type">
                {node.ty || node.type || 'step'}
              </span>
              <span className="mc-mini-step-name">{id}</span>
            </div>
            {idx < order.length - 1 && (
              <div className="mc-mini-step-arrow">&#8594;</div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionZone — the "Actions" section: one compact card per applicable action
// ---------------------------------------------------------------------------

// ActionZone renders the whole "Actions" section (heading + hr + cards) for the cell's current status,
// or null when the status has no applicable action (planned/ready/working/completed) — so the section
// is hidden entirely, heading and separator included. Each action is its own compact card; every word
// of every button label is capitalized. The free-form input case keeps its options + textarea because
// the underlying answerInput call needs the typed reply. Behavior is unchanged: each handler calls the
// client then onActionDone / onLocalStatus.
function ActionZone({ cell, missionId, client, onActionDone, onLocalStatus }) {
  const props = cell.props || {};
  const status = props.status || 'draft';
  const [answer, setAnswer] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState(null);

  const runAsync = async (fn) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      if (onActionDone) onActionDone();
    } catch (e) {
      setActionError(e && e.message ? e.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  // Activate / retry both move the cell to "planned"; reflect that locally so the
  // panel and graph update in place without a reload.
  const activate = () => runAsync(async () => {
    await client.activateCell(missionId, cell.element_id);
    if (onLocalStatus) onLocalStatus('planned');
  });

  // Build the applicable action card(s) for this status. An empty list ⇒ the whole section is hidden.
  // isActionCardList is false only for the free-form input case, which renders its own rich Card
  // instead of a plain ActionCard row, so it isn't wrapped in the shared <ActionCards> frame.
  let cards = [];
  let isActionCardList = true;

  if (status === 'draft') {
    cards = [
      <ActionCard key="activate" helper="Wire dependencies, then activate this cell">
        <Button variant="success" size="sm" disabled={busy} onClick={activate}>
          <GoPlay /> {busy ? 'Activating…' : 'Activate Cell'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'failed') {
    cards = [
      <ActionCard key="retry" helper="A step errored — see the meta-thread for details">
        <Button variant="success" size="sm" disabled={busy} onClick={activate}>
          {busy ? 'Retrying…' : 'Retry'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'input_needed') {
    const inputMeta = props.input_needed || {};
    const kind = inputMeta.kind || 'question';

    if (kind === 'review') {
      // A review gate exposes two actions — approve, or request changes — so it splits into two cards.
      cards = [
        <ActionCard key="approve" helper="Approve the change">
          <Button
            variant="success"
            size="sm"
            disabled={busy}
            onClick={() => runAsync(() => client.answerInput(missionId, cell.element_id, 'approved'))}
          >
            {busy ? '…' : 'Approve'}
          </Button>
        </ActionCard>,
        <ActionCard key="changes" helper="Request changes — loops back to implement">
          <Button
            variant="danger"
            size="sm"
            disabled={busy}
            onClick={() => runAsync(() => client.answerInput(missionId, cell.element_id, 'changes'))}
          >
            {busy ? '…' : 'Request Changes'}
          </Button>
        </ActionCard>,
      ];
    } else {
      const mode = inputMeta.mode || 'in_flight';
      const question = inputMeta.question || inputMeta.q || 'Input required.';
      const opts = inputMeta.opts || inputMeta.options || [];

      // Free-form / choice input keeps its options + textarea (answerInput needs the reply); the submit
      // button sits beside a muted hint to match the compact card idiom.
      isActionCardList = false;
      cards = [
        <Card className="mb-2" key="answer">
          <Card.Body>
            <p className="text-muted mb-1" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              needs input · <code>{kind}</code> · {mode === 'in_flight' ? 'in-flight — resumes this step' : 'gate'}
            </p>
            <p className="mb-2" style={{ fontSize: '0.88rem', fontWeight: 600 }}>{question}</p>
            {opts.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {opts.map((o, i) => (
                  <Button
                    key={i}
                    variant={answer === o ? 'success' : 'light'}
                    size="sm"
                    onClick={() => setAnswer(o)}
                  >
                    {o}
                  </Button>
                ))}
              </div>
            )}
            <Form.Control
              as="textarea"
              size="sm"
              rows={2}
              placeholder="Reply or add detail…"
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <div className="d-flex align-items-center justify-content-between" style={{ gap: 12 }}>
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>Send your reply to resume the cell.</span>
              <Button
                variant="success"
                size="sm"
                disabled={busy || !answer.trim()}
                onClick={() => runAsync(() => client.answerInput(missionId, cell.element_id, answer.trim()))}
                style={{ flexShrink: 0 }}
              >
                {busy ? 'Sending…' : 'Answer Input'}
              </Button>
            </div>
          </Card.Body>
        </Card>,
      ];
    }
  }

  if (cards.length === 0) return null;

  return (
    <>
      <Card.Text as="h6">Actions</Card.Text>
      {isActionCardList ? <ActionCards>{cards}</ActionCards> : cards}
      {actionError && (
        <Alert variant="warning" style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>{actionError}</Alert>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// CellPanel — main panel component
// ---------------------------------------------------------------------------

class CellPanel extends Component {
  constructor(props) {
    super(props);
    const cellProps = (props.cell && props.cell.props) || {};
    this._titleRef = React.createRef();
    this.state = {
      metaItems: [],
      metaLoading: false,
      titleDraft: cellProps.title || '',
      descriptionDraft: cellProps.description || '',
    };
  }

  componentDidMount() {
    this._mounted = true;
    this._loadMetaThread();
    this._autoGrowTitle();
  }

  componentDidUpdate(prevProps) {
    // Re-fetch meta-thread and re-seed editable drafts only when the selected cell's identity changes
    if ((prevProps.cell && prevProps.cell.element_id) !== (this.props.cell && this.props.cell.element_id)) {
      this._loadMetaThread();
      this._seedDrafts();
    }
    // Keep the title field sized to its content (covers seeding + draft-mode toggles).
    this._autoGrowTitle();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  async _loadMetaThread() {
    const { cell, client } = this.props;
    if (!cell || !client) return;

    this.setState({ metaLoading: true });
    try {
      const items = await client.listMetaThread(cell.element_id);
      if (!this._mounted) return;
      this.setState({ metaItems: items || [], metaLoading: false });
    } catch (_) {
      if (!this._mounted) return;
      this.setState({ metaItems: [], metaLoading: false });
    }
  }

  // Seed the editable title/description drafts from the current cell props.
  _seedDrafts() {
    const cellProps = (this.props.cell && this.props.cell.props) || {};
    this.setState({
      titleDraft: cellProps.title || '',
      descriptionDraft: cellProps.description || '',
    });
  }

  // Merge a prop change into the store locally (no reload) so the graph + panel
  // reflect it immediately.
  _applyLocalProps(changed) {
    const { cell, missionId, onApplyLocal } = this.props;
    if (!onApplyLocal || !cell) return;
    onApplyLocal(s => upsertCell(s, missionId, {
      ...cell,
      props: { ...(cell.props || {}), ...changed },
    }));
  }

  async _saveTitle() {
    const { cell, missionId, client } = this.props;
    if (!cell || !client) return;
    const next = this.state.titleDraft;
    const current = (cell.props && cell.props.title) || '';
    if (next === current) return;
    try {
      await client.setCellProps(missionId, cell.element_id, { title: next });
      this._applyLocalProps({ title: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveDescription() {
    const { cell, missionId, client } = this.props;
    if (!cell || !client) return;
    const next = this.state.descriptionDraft;
    const current = (cell.props && cell.props.description) || '';
    if (next === current) return;
    try {
      await client.setCellProps(missionId, cell.element_id, { description: next });
      this._applyLocalProps({ description: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Persist the draft cell's selected workflow. Mirrors _saveTitle/_saveDescription:
  // skip when unchanged, write via setCellProps, then reflect locally. The empty
  // option clears the assignment (null). cell_workflow is the canonical server key
  // (see client.addCell), so it is used on the wire and locally alike.
  async _saveCellWorkflow(value) {
    const { cell, missionId, client } = this.props;
    if (!cell || !client) return;
    const selected = value || '';
    const current = (cell.props && cell.props.cell_workflow) || '';
    if (selected === current) return;
    const next = selected || null;
    try {
      await client.setCellProps(missionId, cell.element_id, { cell_workflow: next });
      this._applyLocalProps({ cell_workflow: next });
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  // Grow the title textarea to fit its content (no inner scrollbar). Accepts the
  // live event target during typing, otherwise falls back to the mounted ref.
  _autoGrowTitle(el) {
    const node = el || this._titleRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }

  render() {
    const { cell, missionId, client, onClose, templates } = this.props;
    const { metaItems, metaLoading, titleDraft, descriptionDraft } = this.state;

    if (!cell) return null;

    const props = cell.props || {};
    const status = props.status || 'draft';
    const title = props.title || cell.element_id;
    const description = props.description || '';
    const cellWorkflowKey = props.cell_workflow || '';
    const workspace = props.workspace || null;
    const currentStep = props.current_step || null;

    // Resolve workflow template (workflows is a LIST of { name, ... })
    const workflows = (templates && templates.workflows) || [];
    const workflow = cellWorkflowKey && Array.isArray(workflows)
      ? (workflows.find(w => w.name === cellWorkflowKey) || null)
      : null;

    // Draft cells pick their workflow from a dropdown; options mirror the add-cell dialog.
    const workflowOptions = [
      { value: '', label: '— none —' },
      ...(Array.isArray(workflows) ? workflows.map(w => w.name).filter(Boolean).map(n => ({ value: n, label: n })) : []),
    ];
    const workflowSelectValue = workflowOptions.find(o => o.value === cellWorkflowKey) || workflowOptions[0];

    return (
      <>
        {onClose && (
          <span className="workflow-controls-pane-right">
            <Button variant="light" onClick={onClose} aria-label="Close panel"><GoX /></Button>
          </span>
        )}

        {/* Static panel header — the cell's own title lives in the Details section below */}
        <Card.Text as="h6">Cell</Card.Text>
        <hr />

        {/* Properties — status / workflow / workspace as the platform's gray-chip table */}
        <Card.Text>
          <table className="properties">
            <tbody>
              <tr>
                <td className="prop-name-column">Status</td>
                <td><StatusPill status={status} /></td>
              </tr>
              <tr>
                <td className="prop-name-column">Workflow</td>
                <td className="prop-value-column">
                  {cellWorkflowKey ? <code>{cellWorkflowKey}</code> : <span className="text-muted">—</span>}
                </td>
              </tr>
              <tr>
                <td className="prop-name-column">Workspace</td>
                <td className="prop-value-column">
                  {workspace ? <code>{workspace}</code> : <span className="text-muted">inherited</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </Card.Text>

        {/* Actions — one compact card per applicable action; the whole section hides when none apply */}
        <ActionZone
          cell={cell}
          missionId={missionId}
          client={client}
          onActionDone={() => this._loadMetaThread()}
          onLocalStatus={(status) => this._applyLocalProps({ status })}
        />

        {/* Details — title + description, editable only while the cell is a draft, read-only afterwards */}
        <Card.Text as="h6">Details</Card.Text>
        <hr />
        {status === 'draft' ? (
          <>
            <Form.Group>
              <Form.Control
                ref={this._titleRef}
                as="textarea"
                rows={1}
                size="sm"
                value={titleDraft}
                placeholder="Cell title…"
                style={{ resize: 'none', overflow: 'hidden' }}
                onChange={e => { this.setState({ titleDraft: e.target.value }); this._autoGrowTitle(e.target); }}
                onBlur={() => this._saveTitle()}
              />
              <Form.Text muted>
                A short, descriptive name for this cell.
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <AceEditor
                mode="markdown"
                theme="monokai"
                width="100%"
                wrapEnabled
                minLines={4}
                maxLines={12}
                value={descriptionDraft}
                onChange={value => this.setState({ descriptionDraft: value })}
                onBlur={() => this._saveDescription()}
              />
              <Form.Text muted>
                Optional notes or context for this cell.
              </Form.Text>
            </Form.Group>
          </>
        ) : (
          <>
            <p className="font-weight-bold">{title}</p>
            <Card.Text>
              {description || <span className="text-muted">No description provided.</span>}
            </Card.Text>
          </>
        )}

        <Card.Text as="h6">Cell Workflow</Card.Text>
        <hr />
        {status === 'draft' ? (
          <Form.Group>
            <Select
              options={workflowOptions}
              value={workflowSelectValue}
              onChange={opt => this._saveCellWorkflow(opt ? opt.value : '')}
              menuPortalTarget={document.body}              placeholder="— none —"
            />
            <Form.Text muted>
              Associate this draft cell with a workflow procedure.
            </Form.Text>
          </Form.Group>
        ) : (
          <>
            <WorkflowMiniGraph workflow={workflow} currentStep={currentStep} />
            {workflow && (
              <small className="text-muted d-block mb-3">Orchestrator interprets it; current step is highlighted.</small>
            )}
          </>
        )}

        <Card.Text as="h6">Meta-thread</Card.Text>
        <hr />
        <MetaThread items={metaItems} loading={metaLoading} />
      </>
    );
  }
}

export default CellPanel;
